
import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { LogOut, User, Menu, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { useIsMobile } from '@/hooks/use-mobile';

const Navbar = () => {
  const { user, profile, signOut } = useAuth();
  const [signOutLoading, setSignOutLoading] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const isMobile = useIsMobile();

  // Navigation links array for easy management
  const navigationLinks = [
    { to: '/marketplace', label: 'Marketplace' },
    { to: '/auctions', label: 'Auctions' },
    { to: '/breeders', label: 'Breeders' }
  ];

  const handleSignOut = async () => {
    setSignOutLoading(true);
    try {
      await signOut();
      toast({
        title: "Signed Out",
        description: "You have been successfully signed out.",
      });
      navigate('/');
    } catch (error) {
      toast({
        title: "Sign Out Failed",
        description: "An error occurred while signing out.",
        variant: "destructive",
      });
    } finally {
      setSignOutLoading(false);
    }
  };

  const handleDashboard = () => {
    if (profile) {
      const dashboardPath = profile.user_type === 'breeder' 
        ? '/breeder-dashboard' 
        : '/buyer-dashboard';
      navigate(dashboardPath);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const isCurrentPage = (path: string) => {
    return location.pathname === path;
  };

  return (
    <nav className="bg-white shadow-sm border-b">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side - Logo and Navigation Links */}
          <div className="flex items-center space-x-12">
            <Link to="/">
              <h1 className="text-3xl font-extrabold">
                <span className="text-black">ur</span>
                <span className="text-green-800">Cattle</span>
              </h1>
            </Link>
            {/* Desktop Navigation Links */}
            <div className="hidden md:flex items-center space-x-8">
              {navigationLinks.map((link) => (
                <Link 
                  key={link.to}
                  to={link.to} 
                  className={`transition-colors ${
                    isCurrentPage(link.to) 
                      ? 'text-green-800 font-medium' 
                      : 'text-gray-700 hover:text-green-800'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
            </div>
          </div>

          {/* Desktop Auth buttons */}
          <div className="hidden md:flex items-center space-x-4">
            {user ? (
              <>
                <Button 
                  onClick={handleDashboard}
                  className="bg-green-700 hover:bg-green-800"
                  disabled={!profile}
                >
                  Dashboard
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleSignOut}
                  disabled={signOutLoading}
                >
                  {signOutLoading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                  ) : null}
                  Sign Out
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="outline">Login</Button>
                </Link>
                <Link to="/signup">
                  <Button className="bg-green-700 hover:bg-green-800">Sign Up</Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col space-y-4">
              {/* Mobile Navigation Links */}
              {navigationLinks.map((link) => (
                <Link 
                  key={link.to}
                  to={link.to} 
                  onClick={closeMobileMenu}
                  className={`px-4 py-2 transition-colors ${
                    isCurrentPage(link.to) 
                      ? 'text-green-800 font-medium bg-green-50' 
                      : 'text-gray-700 hover:text-green-800 hover:bg-gray-50'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              
              {/* Mobile Auth buttons */}
              <div className="px-4 pt-2 border-t border-gray-200">
                {user ? (
                  <div className="flex flex-col space-y-3">
                    <Button 
                      onClick={() => {
                        handleDashboard();
                        closeMobileMenu();
                      }}
                      className="w-full bg-green-700 hover:bg-green-800"
                      disabled={!profile}
                    >
                      Dashboard
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        handleSignOut();
                        closeMobileMenu();
                      }}
                      disabled={signOutLoading}
                      className="w-full"
                    >
                      {signOutLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                      ) : null}
                      Sign Out
                    </Button>
                  </div>
                ) : (
                  <div className="flex flex-col space-y-3">
                    <Link to="/login" onClick={closeMobileMenu}>
                      <Button variant="outline" className="w-full">Login</Button>
                    </Link>
                    <Link to="/signup" onClick={closeMobileMenu}>
                      <Button className="w-full bg-green-700 hover:bg-green-800">Sign Up</Button>
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;
